import React, { useContext, useState } from 'react';
import { DataContext } from '../../stores/crm-deal-store';
import { DataContext as SharedDataContext } from '../../stores/crm-store';
import { DataContext as PersonDataContext } from '../../stores/crm-person-store';

import { HandleAddDeal } from './requests/DealRequests';

import GenericFormModal from './shared/GenericFormModal';
import EditText from './shared/field_inputs/text/EditText';
import EditItem from './shared/field_inputs/item/EditItem';
import EditSelect from './shared/field_inputs/select/EditSelect';
import EditSelectPipelineStage from './shared/field_inputs/pipeline/stage/EditSelectPipelineStage';
import { createGetSuggestionsFromApiUrl } from './shared/field_inputs/HelpMethods';
import { CalculateDealSettings } from './shared/Functions';
import { findPipelineIdPipelineStageIndex } from './shared/Functions';
import { ADD_DEAL, ADD_DEAL_CONTACT_PERSON_LABEL, ADD_DEAL_CUSTOMER_LABEL, ADD_DEAL_TITLE_LABEL } from '../../i18n/keyConstants';
import { useTranslation } from 'react-i18next';


const tryPrependInKanbanColumn = (deal, store, dispatch) => {
    const pipelines = store.pipelines;
    const [pipelineId, stageIndex] = findPipelineIdPipelineStageIndex(pipelines, deal.pipelineStage.id);
    if (pipelineId && stageIndex >= 0) {
        const pipeline = pipelines[pipelineId];
        pipeline.requestObject.amount += 1;
        pipeline.viewSettings = CalculateDealSettings(pipeline.viewSettings, pipeline.requestObject);
        pipeline.pipelineStages[stageIndex].deals.splice(0, 0, deal);

        dispatch({
            type: 'UPDATE_FETCH_PIPES',
            pipelines: pipelines,
        });
    }
};

const AddDeal = ({ initialValues, pipelineStage, position }) => {
    const { t } = useTranslation('addDeal');
    
    // Set initial values
    let label = '+ Deal'
    let buttonType = 'btn-success'
    if (position == 'column') {
        label = '+';
        buttonType = 'btn-block';
    };

    const [customer, setCustomer] = useState({});
    const { dispatch, store } = useContext(DataContext);
    const { dispatch: personDealDispatch } = useContext(PersonDataContext);
    const {
        store: { pipelineOptions: pipelines },
    } = useContext(SharedDataContext);

    let stage = pipelineStage ?? 0



    const defaultFormValues = {
        pipeline: pipelines ? pipelines[0] : null,
        pipelineStage: pipelines ? pipelines[0].pipelineStages[stage] : null,
        ...initialValues,
    };

    if (customer && customer.id) {
        defaultFormValues['customer'] = customer;
    }
        

    const valueSet = (propName, value) => {
        if (propName === 'customer'){
            if (value != undefined && value != null && value.id){
                setCustomer(value);
            }else {
                setCustomer({});
            }
        }
    }

    // Mock deal
    // const defaultFormValues = {
    //     pipeline: pipelines ? pipelines[0] : null,
    //     pipelineStage: pipelines ? pipelines[0].pipelineStages[0] : null,
    //     name: 'Test deal',
    //     value: 1234,
    //     contact: {
    //         id: '00000108-0000-0000-0000-000000000000',
    //         name: 'Axel Strömberg',
    //         detailedName: 'Axel Strömberg (Consid)',
    //         underlinedDetailedName: '<u>Axel</u> <u>Strömberg</u> (Consid)',
    //         link: '/crmPersonDetails/00000108-0000-0000-0000-000000000000/info',
    //         linkedOrganizationItem: {
    //             id: '00000003-0000-0000-0000-000000000000',
    //             name: 'Consid',
    //             detailedName: 'Consid',
    //         },
    //     },
    //     customer: {
    //         id: '00000003-0000-0000-0000-000000000000',
    //         name: 'Consid',
    //         detailedName: 'Consid',
    //     },
    // };

    const formComponents = [
        {
            propName: 'customer',
            label: `${t(ADD_DEAL_CUSTOMER_LABEL)}`,
            validate: (v) => (!v ? 'Välj kund.' : ''),
            bindOnChange: (values, changedPropName) => {
                if (changedPropName === 'contact') {
                    if (values.contact?.linkedOrganizationItem) {
                        return values.contact.linkedOrganizationItem;
                    }
                }
            },
            component: (
                <EditItem
                    iconName='organization'
                    isCreationPermitted
                    isClearable
                    autoFocus
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmOrganizationFront/getOrganizationSuggestions')}
                />
            ),
        },
        {
            propName: 'contact',
            label: `${t(ADD_DEAL_CONTACT_PERSON_LABEL)}`,
            validate: (v, formValues) => {
                if (!v) {
                    return '';
                } else if (formValues.customer?.id) {
                    if (formValues.contact.linkedOrganizationItem !== undefined && formValues.contact.linkedOrganizationItem.id !== formValues.customer.id) {
                        return 'Kontaktpersonen måste arbeta hos vald kund.';
                    }
                }
                return '';
            },
            component: (
                <EditItem
                    iconName='person'
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/crmPersonFront/getPersonSuggestions', customer?.id)}
                    isCreationPermitted
                    isClearable
                />
            ),
        },
        {
            propName: 'name',
            label: `${t(ADD_DEAL_TITLE_LABEL)}`,
            validate: (v) => (!v || !v.length ? 'Fyll i titel.' : ''),
            component: <EditText />,
        },
        // {
        //     propName: 'value',
        //     label: 'Värde',
        //     validate: (v) => (isNaN(v) ? 'Måste vara ett tal' : ''),
        //     component: <EditNumber isCurrency />,
        // },
        {
            propName: 'pipeline',
            label: 'Pipeline',
            component: <EditSelect options={pipelines} />,
        },
        {
            propName: 'pipelineStage',
            label: 'Stage',
            bindOnChange: (values, changedPropName) => {
                if (!changedPropName === 'pipeline') return;
                if (!values.pipeline.pipelineStages.some((s) => s.id === values.pipelineStage.id)) {
                    return values.pipeline.pipelineStages[0];
                }
            },
            component: <EditSelectPipelineStage stages={defaultFormValues.pipeline?.pipelineStages} />,
        },
    ];

    const handleAdd = async (deal) => {
        const response = await HandleAddDeal(deal);
        if (response.success) {
            personDealDispatch({
                type: 'UPDATE_ACTIVITY_DEAL_ALTERNATIVES',
                payload: deal,
            });
            tryPrependInKanbanColumn(response.data, store, dispatch);
        }
        return response;
    };
 

    return (
        <GenericFormModal
            label={label}
            description={t(ADD_DEAL)}
            formComponents={formComponents}
            handleAdd={handleAdd}
            onValueSet={valueSet}
            defaultFormValues={defaultFormValues}
            buttonType={buttonType}
        />
    );
};

AddDeal.defaultProps = {
    initialValues: {},
};

export default AddDeal;
